import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDeps } from '../app/deps';
import { useBooks } from '../app/session';

interface Capture {
  isOpen: boolean;
  open(): void;
  close(): void;
  /** Captures for these books waiting on the phone (the Capture disc's count). */
  waiting: number;
}

const CaptureSheet = lazy(() => import('./CaptureSheet').then((m) => ({ default: m.CaptureSheet })));

const CaptureContext = createContext<Capture>({ isOpen: false, open: () => undefined, close: () => undefined, waiting: 0 });

/** The ⊕ sheet opens over any screen (spec 3A §6.1). The sheet loads as its own chunk the first time it opens. */
export function CaptureProvider({ children }: { children: ReactNode }) {
  const { outbox } = useDeps();
  const { current } = useBooks();
  const [isOpen, setOpen] = useState(false);
  const [waiting, setWaiting] = useState(0);
  useEffect(() => {
    if (!current) {
      setWaiting(0);
      return;
    }
    let live = true;
    const count = () =>
      void outbox
        .queued(current.orgId)
        .then((q) => {
          if (live) setWaiting(q.length);
        })
        .catch(() => undefined);
    count();
    const stop = outbox.subscribe(count);
    return () => {
      live = false;
      stop();
    };
  }, [current, outbox]);
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ isOpen, open, close, waiting }), [isOpen, open, close, waiting]);
  return (
    <CaptureContext.Provider value={value}>
      {children}
      {isOpen && current && (
        <Suspense fallback={null}>
          <CaptureSheet onClose={close} />
        </Suspense>
      )}
    </CaptureContext.Provider>
  );
}

export function useCapture() {
  return useContext(CaptureContext);
}
